
import { useState } from 'react';
import { aiTutorials } from '../../../../mocks/aiDramaTools';

export default function TutorialSection() {
  const [activeDifficulty, setActiveDifficulty] = useState<string>('all');
  const [expandedTutorial, setExpandedTutorial] = useState<string | null>(null);

  const difficulties = [
    { id: 'all', label: 'All Levels', color: 'bg-gray-100 text-gray-600' },
    { id: 'Beginner', label: 'Beginner', color: 'bg-emerald-100 text-emerald-700' },
    { id: 'Intermediate', label: 'Intermediate', color: 'bg-amber-100 text-amber-700' },
    { id: 'Advanced', label: 'Advanced', color: 'bg-red-100 text-red-700' },
  ];

  const difficultyColor: Record<string, string> = {
    Beginner: 'bg-emerald-500',
    Intermediate: 'bg-amber-500',
    Advanced: 'bg-red-500',
  };

  const filtered = activeDifficulty === 'all'
    ? aiTutorials
    : aiTutorials.filter((t) => t.difficulty === activeDifficulty);

  const formatViews = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center bg-orange-100 rounded-lg">
              <i className="ri-graduation-cap-line text-orange-500 text-sm"></i>
            </span>
            Step-by-Step Tutorials
          </h2>
          <p className="text-sm text-gray-500 mt-1">Learn AI drama creation from beginner to advanced</p>
        </div>
      </div>

      {/* Difficulty Filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        {difficulties.map((d) => (
          <button
            key={d.id}
            onClick={() => setActiveDifficulty(d.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeDifficulty === d.id
                ? 'bg-orange-500 text-white'
                : d.color + ' hover:opacity-80'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Tutorial Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {filtered.map((tutorial) => {
          const isExpanded = expandedTutorial === tutorial.id;
          return (
            <div
              key={tutorial.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-orange-200 transition-all duration-300 group"
            >
              {/* Thumbnail */}
              <div className="relative w-full h-[180px] overflow-hidden">
                <img
                  src={tutorial.image}
                  alt={tutorial.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                {/* Difficulty Badge */}
                <span className={`absolute top-3 right-3 px-2.5 py-1 ${difficultyColor[tutorial.difficulty]} text-white text-xs font-medium rounded-full`}>
                  {tutorial.difficulty}
                </span>

                {/* Duration */}
                <span className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/70 text-white text-xs rounded-md flex items-center gap-1">
                  <i className="ri-time-line"></i>{tutorial.duration}
                </span>

                {/* Title overlay */}
                <div className="absolute bottom-3 left-3 right-16">
                  <h3 className="text-white font-semibold text-[15px] leading-snug line-clamp-2 drop-shadow-md">
                    {tutorial.title}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">{tutorial.description}</p>

                {/* Author & Stats */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <img
                      src={tutorial.author.avatar}
                      alt={tutorial.author.username}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-xs text-gray-600 font-medium">{tutorial.author.username}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>{formatViews(tutorial.views)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-fill text-red-400"></i>{formatViews(tutorial.likes)}
                    </span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {tutorial.tags.slice(0, 4).map((tag, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Expand Steps */}
                <button
                  onClick={() => setExpandedTutorial(isExpanded ? null : tutorial.id)}
                  className="w-full flex items-center justify-center gap-1 py-2 text-xs font-medium text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`ri-${isExpanded ? 'arrow-up' : 'list-check'}-line`}></i>
                  {isExpanded ? 'Hide Steps' : `View ${tutorial.steps.length} Steps`}
                </button>

                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    {tutorial.steps.map((step, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <span className="w-5 h-5 flex items-center justify-center bg-orange-500 text-white text-[10px] font-bold rounded-full flex-shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        <span className="text-sm text-gray-700">{step}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
